<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TaccRemindersAPI from 'dashboard/api/taccReminders';
import AgentsAPI from 'dashboard/api/agents';
import ReminderModal from './ReminderModal.vue';

const OPEN_REMINDER_CREATE = 'TACC_OPEN_REMINDER_CREATE';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const reminders = ref([]);
const agents = ref([]);
const isLoading = ref(false);
const isLoadingAgents = ref(false);
const errorMessage = ref('');
const modalRef = ref(null);

const hasReminders = computed(() => reminders.value.length > 0);

const formatReminderTime = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

const loadReminders = async () => {
  if (!props.conversationId) return;
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await TaccRemindersAPI.getReminders(props.conversationId);
    reminders.value = response.data.reminders || [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      'Could not load reminders.';
  } finally {
    isLoading.value = false;
  }
};

const normalizeAgent = agent => {
  const source = agent.user || agent;
  return {
    id: source.id || agent.user_id,
    available_name: source.available_name,
    name: source.name,
    email: source.email,
  };
};

const loadAgents = async () => {
  isLoadingAgents.value = true;
  try {
    const { data } = await AgentsAPI.get();
    const payload = Array.isArray(data)
      ? data
      : data.payload || data.agents || [];
    agents.value = payload.map(normalizeAgent).filter(agent => agent.id);
  } catch (error) {
    agents.value = [];
  } finally {
    isLoadingAgents.value = false;
  }
};

const openCreate = () => {
  modalRef.value?.open('create');
};

const openEdit = reminder => {
  modalRef.value?.open('edit', reminder);
};

const onSaved = reminder => {
  const index = reminders.value.findIndex(item => item.id === reminder.id);
  if (index >= 0) {
    reminders.value.splice(index, 1, reminder);
  } else {
    reminders.value.push(reminder);
  }
  reminders.value.sort(
    (a, b) => new Date(a.remind_at).getTime() - new Date(b.remind_at).getTime()
  );
  useAlert('Reminder saved.');
};

const deleteReminder = async reminder => {
  if (
    !window.confirm(
      `Delete reminder "${reminder.title || 'this reminder'}"?\n\nThis reminder will be removed from the conversation.`
    )
  ) {
    return;
  }

  try {
    await TaccRemindersAPI.deleteReminder(props.conversationId, reminder.id);
    reminders.value = reminders.value.filter(item => item.id !== reminder.id);
    useAlert('Reminder deleted.');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Could not delete reminder.');
  }
};

const onOpenCreateEvent = conversationId => {
  if (String(conversationId) !== String(props.conversationId)) return;
  openCreate();
};

watch(
  () => props.conversationId,
  () => {
    reminders.value = [];
    loadReminders();
  }
);

onMounted(() => {
  emitter.on(OPEN_REMINDER_CREATE, onOpenCreateEvent);
  loadReminders();
  loadAgents();
});

onUnmounted(() => {
  emitter.off(OPEN_REMINDER_CREATE, onOpenCreateEvent);
});
</script>

<template>
  <div class="px-3 py-3">
    <div v-if="isLoading" class="flex min-h-28 items-center justify-center">
      <Spinner />
    </div>

    <div
      v-else-if="errorMessage"
      class="flex flex-col items-center gap-3 py-5 text-center"
    >
      <p class="mb-0 text-sm text-n-ruby-10">{{ errorMessage }}</p>
      <Button xs outline color="slate" label="Retry" @click="loadReminders" />
    </div>

    <div
      v-else-if="!hasReminders"
      class="flex flex-col items-center gap-2.5 py-5 text-center"
    >
      <span class="i-lucide-bell size-6 text-n-slate-10" />
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        Nothing scheduled
      </p>
      <p class="mb-0 max-w-64 text-xs leading-5 text-n-slate-11">
        Set a reminder to follow up, or snooze this conversation until later.
      </p>
      <Button
        xs
        color="blue"
        icon="i-lucide-plus"
        label="Add reminder"
        @click="openCreate"
      />
    </div>

    <div v-else class="flex flex-col gap-2">
      <div
        v-for="reminder in reminders"
        :key="reminder.id"
        class="flex items-start gap-2 rounded-lg bg-n-alpha-2 p-3"
      >
        <span class="i-lucide-clock-3 mt-0.5 size-4 shrink-0 text-n-brand" />
        <div class="min-w-0 flex-1">
          <p class="mb-1 truncate text-sm font-medium text-n-slate-12">
            {{ reminder.title }}
          </p>
          <p class="mb-0 truncate text-xs text-n-slate-11">
            {{ formatReminderTime(reminder.remind_at) }} ·
            {{ reminder.assignee?.name || 'Me' }}
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <button
            type="button"
            class="flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
            title="Edit reminder"
            @click="openEdit(reminder)"
          >
            <span class="i-lucide-pencil size-3.5" />
          </button>
          <button
            type="button"
            class="flex size-7 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-10"
            title="Delete reminder"
            @click="deleteReminder(reminder)"
          >
            <span class="i-lucide-trash-2 size-3.5" />
          </button>
        </div>
      </div>

      <Button
        faded
        blue
        sm
        icon="i-lucide-plus"
        label="Add reminder"
        @click="openCreate"
      />
    </div>

    <ReminderModal
      ref="modalRef"
      :conversation-id="conversationId"
      :agents="agents"
      :loading-agents="isLoadingAgents"
      @saved="onSaved"
    />
  </div>
</template>
